"use client";

/**
 * FAQ accordion — redesigned to match the centered single-column reference.
 *
 * Visual model:
 *   - No boxed cards. Each item is a row with a thin hairline divider
 *     below; the title sits at left, a cyan triangle indicator at right.
 *   - Expanding rotates the triangle 180° and reveals the body with a
 *     height transition (grid-rows hack so we don't need to measure).
 *   - One item open at a time, click again to close.
 */

import { useState } from "react";

type Item = { q: string; a: string };

export default function FaqAccordion({ items }: { items: Item[] }) {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <ul className="divide-y divide-[var(--stroke)]/70">
      {items.map((item, i) => {
        const isOpen = open === i;
        return (
          <li key={`${item.q}-${i}`}>
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : i)}
              aria-expanded={isOpen}
              className="group flex w-full items-center justify-between gap-6 py-6 text-left transition-colors"
            >
              <span className="pr-2 text-lg font-semibold leading-snug text-[var(--text)] md:text-xl">
                {item.q}
              </span>
              {/* Solid filled downward triangle, matches the reference
                  image. We render the SVG without a containing pill so the
                  triangle itself is the indicator; `rotate-180` on open
                  points it upward. A subtle drop-shadow lifts it off the
                  background like a glow. */}
              <span
                aria-hidden
                className={`inline-flex h-7 w-7 shrink-0 items-center justify-center transition-transform duration-300 ${
                  isOpen ? "rotate-180" : "rotate-0"
                }`}
              >
                <svg
                  width="20"
                  height="16"
                  viewBox="0 0 20 16"
                  /* 3-point polygon → equilateral-ish triangle pointing down */
                  style={{ filter: "drop-shadow(0 0 6px rgba(11,201,240,0.45))" }}
                >
                  <polygon points="10,16 0,0 20,0" fill="var(--accent)" />
                </svg>
              </span>
            </button>

            {/* Body. `grid-rows-[0fr]` → `grid-rows-[1fr]` is the canonical
                pattern for height-auto transitions without JS measurement. */}
            <div
              className={`grid transition-all duration-300 ease-out ${
                isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
              }`}
            >
              <div className="overflow-hidden">
                <p className="pb-6 pr-10 text-base leading-relaxed text-[var(--muted)]">
                  {item.a}
                </p>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
